import type {
  AccessRole,
  CreateTemplateShareRequest,
  CreateTemplateShareResponse,
  Database,
  RedeemTemplateShareRequest,
  RedeemTemplateShareResponse,
  TemplateRole,
} from '@rbf/shared';

import { supabase } from './supabase';
import { powerSync } from './powersync/db';

/**
 * Data layer for RBF. All reads/writes go through the LOCAL PowerSync SQLite DB
 * — so everything works offline and PowerSync reconciles with Supabase on
 * reconnect. Reactive reads use `useQuery` from @powersync/react directly in
 * the screens; this module holds the mutations.
 *
 * Local SQLite has no boolean (`checked` is 0/1) and no server-side defaults,
 * so we generate ids and timestamps on the client.
 *
 * Row shapes are DERIVED from the generated `Database` types (the single
 * structural source of truth, regenerated via `pnpm gen:types`), with explicit
 * overrides only where the local SQLite read shape genuinely differs from the
 * Postgres wire shape — so adding or renaming a column propagates here for free.
 */

type Row<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row'];

export type Template = Row<'templates'>;

export type TemplateItem = Row<'template_items'>;

export type Instance = Row<'instances'>;

// Postgres `checked` is boolean; SQLite has no boolean, so the local row stores
// 0/1 and reads it back as a number (coerced at the upload boundary — see
// connector.ts). Every other column matches the generated row.
export type InstanceItem = Omit<Row<'instance_items'>, 'checked'> & { checked: number };

export type InstanceItemNote = Row<'instance_item_notes'>;

/**
 * A template member's display name, projected per template. The live source of
 * run attribution — join `checked_by` / `author_id` to `user_id` (same
 * `template_id`) to resolve a name offline. See the 20260703000000 migration.
 * Only the three columns the app reads (the `id` PK is not projected).
 */
export type TemplateMemberProfile = Pick<
  Row<'template_member_profiles'>,
  'template_id' | 'user_id' | 'display_name'
>;

/**
 * The slice of the collaborator roster that presence colors derive from:
 * color = position in join order, computed identically on every client
 * (see ADR 0002 and CONTEXT.md: Presence color).
 */
export type TemplateRosterCollaborator = Pick<
  Row<'template_collaborators'>,
  'user_id' | 'joined_at'
>;

/**
 * The owner's entry in the presence-color roster, taken from the templates row
 * itself: only share-link redeemers get template_collaborators rows, so the
 * owner joins the color derivation via created_at — which predates every
 * redemption, putting the owner first.
 */
export type TemplateRosterOwner = Pick<Row<'templates'>, 'owner_id' | 'created_at'>;

function newId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback for runtimes without crypto.randomUUID.
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

function now(): string {
  // Pass-through ISO timestamp; the server may overwrite via triggers.
  return new Date().toISOString();
}

/**
 * Build a partial `UPDATE … SET` from only the provided fields (keys are column
 * names, supplied by callers — never user input). `updated_at = ?` is appended
 * and the row `id` pushed last to match a trailing `WHERE id = ?`. Returns null
 * when nothing was provided, so the caller can skip the write entirely (a no-op
 * doesn't churn updated_at or generate a sync op).
 */
function buildPartialUpdate(
  fields: Record<string, unknown>,
  id: string,
): { sets: string[]; params: unknown[] } | null {
  const sets: string[] = [];
  const params: unknown[] = [];
  for (const [column, value] of Object.entries(fields)) {
    if (value !== undefined) {
      sets.push(`${column} = ?`);
      params.push(value);
    }
  }
  if (sets.length === 0) return null;
  sets.push('updated_at = ?');
  params.push(now(), id);
  return { sets, params };
}

async function currentUserId(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const id = data.session?.user.id;
  if (!id) throw new Error('Not authenticated');
  return id;
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

/** Update the signed-in user's own profile (display name). */
export async function updateProfile(fields: {
  display_name?: string | null;
}): Promise<void> {
  if (fields.display_name === undefined) return;
  const id = await currentUserId();
  await powerSync.execute(
    'UPDATE profiles SET display_name = ?, updated_at = ? WHERE id = ?',
    [fields.display_name, now(), id],
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export async function createTemplate(input: {
  title: string;
  description?: string | null;
  icon?: string | null;
}): Promise<string> {
  const id = newId();
  const owner = await currentUserId();
  const ts = now();
  await powerSync.execute(
    `INSERT INTO templates (id, owner_id, title, description, icon, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [id, owner, input.title, input.description ?? null, input.icon ?? null, ts, ts],
  );
  return id;
}

export async function updateTemplate(
  id: string,
  fields: { title?: string; description?: string | null; icon?: string | null },
): Promise<void> {
  const update = buildPartialUpdate(fields, id);
  if (!update) return;
  await powerSync.execute(
    `UPDATE templates SET ${update.sets.join(', ')} WHERE id = ?`,
    update.params,
  );
}

/**
 * Bump a template's `updated_at` so an edit to its steps re-sorts it as
 * "recently edited" (the list orders by this column). Steps live in a separate
 * `template_items` table, so a step write leaves the parent row untouched unless
 * we do this. Accepts a transaction handle + shared timestamp so callers can
 * fold the touch into the same write as the step mutation.
 */
async function touchTemplate(
  id: string,
  db: { execute: (sql: string, params?: unknown[]) => Promise<unknown> } = powerSync,
  ts: string = now(),
): Promise<void> {
  await db.execute('UPDATE templates SET updated_at = ? WHERE id = ?', [ts, id]);
}

/** The parent template id of a step, or null if the step no longer exists. */
async function templateIdOfItem(itemId: string): Promise<string | null> {
  const row = await powerSync.getOptional<{ template_id: string }>(
    'SELECT template_id FROM template_items WHERE id = ?',
    [itemId],
  );
  return row?.template_id ?? null;
}

export async function deleteTemplate(id: string): Promise<void> {
  // Soft delete: a completed run freezes its snapshot rows, and hard-deleting
  // the template would fire FK `set null` actions against those frozen rows that
  // the freeze triggers reject. Stamping deleted_at hides the template (filtered
  // out of the UI and sync rules) while leaving its rows — and every past run —
  // intact.
  const ts = now();
  await powerSync.execute(
    'UPDATE templates SET deleted_at = ?, updated_at = ? WHERE id = ?',
    [ts, ts, id],
  );
}

export async function addTemplateItem(input: {
  template_id: string;
  label: string;
  notes?: string | null;
}): Promise<string> {
  const id = newId();
  const ts = now();
  await powerSync.writeTransaction(async (tx) => {
    // Append after the current last step. Deriving the position from MAX rather
    // than the item count keeps it collision-free after a mid-list delete leaves
    // a gap (a count would land on a position still in use). Gaps are harmless —
    // only relative order matters, and reorderTemplateItems compacts on drag.
    const row = await tx.get<{ next: number }>(
      'SELECT COALESCE(MAX(position), -1) + 1 AS next FROM template_items WHERE template_id = ?',
      [input.template_id],
    );
    await tx.execute(
      `INSERT INTO template_items (id, template_id, position, label, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [id, input.template_id, row.next, input.label, input.notes ?? null, ts, ts],
    );
    await touchTemplate(input.template_id, tx, ts);
  });
  return id;
}

export async function updateTemplateItem(
  id: string,
  fields: { label?: string; position?: number; notes?: string | null },
): Promise<void> {
  const update = buildPartialUpdate(fields, id);
  if (!update) return;
  const templateId = await templateIdOfItem(id);
  const ts = now();
  await powerSync.writeTransaction(async (tx) => {
    await tx.execute(
      `UPDATE template_items SET ${update.sets.join(', ')} WHERE id = ?`,
      update.params,
    );
    if (templateId) await touchTemplate(templateId, tx, ts);
  });
}

export async function deleteTemplateItem(id: string): Promise<void> {
  const templateId = await templateIdOfItem(id);
  const ts = now();
  await powerSync.writeTransaction(async (tx) => {
    await tx.execute('DELETE FROM template_items WHERE id = ?', [id]);
    if (templateId) await touchTemplate(templateId, tx, ts);
  });
}

/**
 * Rewrite template_items.position to match the given order (0..n-1). Only rows
 * whose position actually changes are written, so a drag that ends where it
 * started is a no-op and doesn't churn updated_at / generate sync ops.
 */
export async function reorderTemplateItems(
  items: { id: string; position: number }[],
): Promise<void> {
  const changed = items
    .map((item, index) => ({ id: item.id, index, position: item.position }))
    .filter(({ index, position }) => index !== position);
  const first = items[0];
  if (changed.length === 0 || !first) return;
  const templateId = await templateIdOfItem(first.id);
  const ts = now();
  await powerSync.writeTransaction(async (tx) => {
    for (const { id, index } of changed) {
      await tx.execute(
        'UPDATE template_items SET position = ?, updated_at = ? WHERE id = ?',
        [index, ts, id],
      );
    }
    if (templateId) await touchTemplate(templateId, tx, ts);
  });
}

// ---------------------------------------------------------------------------
// Instances (runs)
// ---------------------------------------------------------------------------

/**
 * Spin up a run by snapshotting the template's items locally, in one
 * transaction. Done client-side (not via the server RPC) so it works offline.
 */
export async function startInstance(templateId: string): Promise<string> {
  const owner = await currentUserId();
  const instanceId = newId();
  const ts = now();

  const template = await powerSync.getOptional<{
    title: string;
    description: string | null;
  }>('SELECT title, description FROM templates WHERE id = ? AND deleted_at IS NULL', [
    templateId,
  ]);
  if (!template) throw new Error('Template not found');

  const items = await powerSync.getAll<TemplateItem>(
    'SELECT * FROM template_items WHERE template_id = ? ORDER BY position, id',
    [templateId],
  );
  // A run with no steps can't be meaningfully worked, so refuse to create one
  // here — the backstop for every caller, regardless of UI guards.
  if (items.length === 0) {
    throw new Error('Add at least one step before starting a run.');
  }

  await powerSync.writeTransaction(async (tx) => {
    await tx.execute(
      `INSERT INTO instances (id, template_id, owner_id, title, description, status, started_at, completed_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, 'active', ?, NULL, ?, ?)`,
      [instanceId, templateId, owner, template.title, template.description, ts, ts, ts],
    );
    for (const item of items) {
      await tx.execute(
        `INSERT INTO instance_items
           (id, instance_id, template_id, source_template_item_id, position, label, notes, checked, checked_by, checked_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, 0, NULL, NULL, ?, ?)`,
        [
          newId(),
          instanceId,
          templateId,
          item.id,
          item.position,
          item.label,
          item.notes,
          ts,
          ts,
        ],
      );
    }
  });

  return instanceId;
}

// ---------------------------------------------------------------------------
// Sharing. A template owner/admin mints role-tagged invite links; redeeming one
// makes the caller a collaborator, and access to a template carries access to
// all of its runs (see the sync rules). Token mint/redeem are
// server-authoritative Edge Functions — they need the service role to act
// outside the caller's RLS.
// ---------------------------------------------------------------------------

/** Admin mints an invite link at the given role; returns the opaque token. */
export async function createTemplateShare(
  templateId: string,
  role: TemplateRole,
): Promise<string> {
  const body: CreateTemplateShareRequest = { template_id: templateId, role };
  const { data, error } = await supabase.functions.invoke('create-template-share', {
    body,
  });
  if (error) throw error;
  // `invoke` returns `any`; the shape is pinned by the shared edge contract.
  return (data as CreateTemplateShareResponse).token;
}

/**
 * Recipient redeems an invite link, becoming a collaborator at the link's role
 * (a higher existing role is kept). Resolves to the template id to navigate to
 * plus the caller's effective role.
 */
export async function redeemTemplateShare(
  token: string,
): Promise<{ template_id: string; role: AccessRole }> {
  const body: RedeemTemplateShareRequest = { token };
  const { data, error } = await supabase.functions.invoke('redeem-template-share', {
    body,
  });
  if (error) throw error;
  // `invoke` returns `any`; the shape is pinned by the shared edge contract.
  return data as RedeemTemplateShareResponse;
}

// ---------------------------------------------------------------------------
// Access management. These are the app's only direct Data-API calls (everything
// else rides PowerSync or Edge Functions): an admin does NOT sync other users'
// profiles, and template_shares tokens stay off the sync stream, so the roster
// with names and the outstanding links come from SECURITY DEFINER RPCs, and
// removing/re-roling a collaborator or revoking a link acts on rows the admin
// can't reach through the local sync buckets. All are gated server-side (the
// RPCs and delete/update RLS policies check can_admin_template) and require
// connectivity — they're management actions, not offline-first mutations.
// ---------------------------------------------------------------------------

// The RPC result rows are DERIVED from the generated `Returns` shapes; only the
// `role` column (generated as a bare `string`) is narrowed to the domain enum.
type RpcRow<F extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][F]['Returns'] extends readonly (infer R)[] ? R : never;

export type RosterMember = Omit<RpcRow<'template_roster'>, 'role'> & { role: AccessRole };

export type ShareLink = Omit<RpcRow<'template_shares_list'>, 'role'> & {
  role: TemplateRole;
};

/** Admin-only: everyone with access to the template (incl. the owner) + role. */
export async function templateRoster(templateId: string): Promise<RosterMember[]> {
  const { data, error } = await supabase.rpc('template_roster', {
    p_template_id: templateId,
  });
  if (error) throw error;
  // `data` is already typed from the generated `Returns`; the cast only narrows
  // the `role` string to AccessRole.
  return (data ?? []) as RosterMember[];
}

/** Admin-only: the outstanding invite links for the template. */
export async function templateShareList(templateId: string): Promise<ShareLink[]> {
  const { data, error } = await supabase.rpc('template_shares_list', {
    p_template_id: templateId,
  });
  if (error) throw error;
  // See templateRoster: the cast only narrows `role` to TemplateRole.
  return (data ?? []) as ShareLink[];
}

/** Admin removes a collaborator from a template. */
export async function removeTemplateCollaborator(
  templateId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('template_collaborators')
    .delete()
    .eq('template_id', templateId)
    .eq('user_id', userId);
  if (error) throw error;
}

/** Admin changes a collaborator's role. */
export async function setCollaboratorRole(
  templateId: string,
  userId: string,
  role: TemplateRole,
): Promise<void> {
  const { error } = await supabase
    .from('template_collaborators')
    .update({ role })
    .eq('template_id', templateId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Admin revokes a single invite link by id, so it can no longer be redeemed.
 * Collaborators who already joined keep access until individually removed.
 */
export async function revokeTemplateShare(shareId: string): Promise<void> {
  const { error } = await supabase.from('template_shares').delete().eq('id', shareId);
  if (error) throw error;
}

export async function setItemChecked(itemId: string, checked: boolean): Promise<void> {
  const ts = now();
  if (checked) {
    // The server write guard re-stamps checked_by from the caller; the display
    // name is resolved live from template_member_profiles at render time.
    // checked_at is the AUTHORED time and survives upload (the server only bounds
    // it to the run window), so a check made offline keeps the time it was made.
    // The server owns checked_recorded_at — writing it here would be rejected.
    const userId = await currentUserId();
    await powerSync.execute(
      `UPDATE instance_items
         SET checked = 1, checked_by = ?, checked_at = ?, updated_at = ?
       WHERE id = ?`,
      [userId, ts, ts, itemId],
    );
  } else {
    await powerSync.execute(
      `UPDATE instance_items
         SET checked = 0, checked_by = NULL, checked_at = NULL, updated_at = ?
       WHERE id = ?`,
      [ts, itemId],
    );
  }
}

/**
 * Append an immutable log note to a run step. Attributed to the current user by
 * `author_id` (the insert policy pins it to auth.uid()); the display name is
 * resolved live from template_member_profiles at render time. Stamped locally;
 * the note can never be edited or deleted (enforced by RLS + a trigger).
 *
 * created_at is the AUTHORED time and survives upload (the server only bounds it to
 * the run window), so a note written offline keeps its time and its place in the
 * log. The server owns recorded_at — never write it here.
 */
export async function addInstanceItemNote(input: {
  instance_id: string;
  instance_item_id: string;
  body: string;
}): Promise<void> {
  const userId = await currentUserId();
  // Stamp the run's template_id (the sync routing key); the server trigger
  // re-derives it authoritatively on upload, this just keeps the local row whole.
  await powerSync.execute(
    `INSERT INTO instance_item_notes
       (id, instance_id, instance_item_id, author_id, body, created_at, template_id)
     VALUES (?, ?, ?, ?, ?, ?, (SELECT template_id FROM instances WHERE id = ?))`,
    [
      newId(),
      input.instance_id,
      input.instance_item_id,
      userId,
      input.body,
      now(),
      input.instance_id,
    ],
  );
}

/**
 * Freeze a run locally. completed_at is the AUTHORED seal time and the server keeps
 * it (merely clamping it to the run's window), so a run sealed at the gate with no
 * signal still reports when it was sealed rather than when it synced; the server
 * records the ingest instant separately in completed_recorded_at. completed_by is
 * set here so the frozen local row is whole (its display name resolves live from
 * template_member_profiles), but that value is advisory — the write guard overwrites
 * it with the authenticated caller.
 */
export async function completeInstance(id: string): Promise<void> {
  const ts = now();
  const userId = await currentUserId();
  await powerSync.execute(
    `UPDATE instances SET status = 'completed', completed_at = ?, completed_by = ?, updated_at = ? WHERE id = ?`,
    [ts, userId, ts, id],
  );
}
